import { useLazyQuery, useMutation } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import {
  GENERATE_USER_MUTATION,
  MODIFY_USER_MUTATION,
  USER_QUERY
} from '../../graphql/userManagement'
import { lang } from '../../localization'
import { User, UserForCreateAndModify } from '../../types/users'
import UserBasicInfo from './userBasicInfo'
import { UserFooter } from './userFooter'
import UserHeader from './userFormHeader'
import UserRole from './userRole'
import UserSecurity from './userSecurity'
import UserSetting from './userSetting'
// import UserTheme from './userTheme'
import { userFormValidationSchema } from './validations/userFormFormValidation'

type AddUserFormProps = {}

const ADD_USER_FORM_INIT_VALUES: UserForCreateAndModify = {
  active: false,
  activeUntil: '',
  email: '',
  password: '',
  entityName: '',
  failedAttempts: 0,
  lastLogin: '',
  locked: false,
  name: '',
  phoneNumber: '',
  regionalSettings: {
    languagesAvailable: [],
    currenciesAvailable: [],
    defaultCurrency: undefined,
    timezone: '',
    themeCode: ''
  },
  resources: [],
  roleID: '',
  displayRoleID: '',
  twoFactorEnabled: false,
  unlockesIn: '',
  username: '',
  selectedResourceType: {
    label: '',
    labelFA: 'اقامتگاه'
  }
}

export default function UserForm({}: AddUserFormProps) {
  const theme = useTheme()
  const history = useHistory()
  const params = useParams<{ id: string }>()
  const [initialValues, setInitialValues] = useState<UserForCreateAndModify>(
    ADD_USER_FORM_INIT_VALUES
  )

  const [getUser, { loading: loadingForUsers }] = useLazyQuery<{
    user: User
  }>(USER_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const user = res.user
      setInitialValues((prev) => ({
        ...prev,

        active: user.active,
        password: '',
        activeUntil: user.activeUntil,
        email: user.email,
        failedAttempts: user.failedAttempts,
        lastLogin: user.lastLogin,
        locked: user.locked,
        name: user.name,
        phoneNumber: user.phoneNumber,
        resources: user.resources,
        roleID: user.roleID,
        displayRoleID: user.displayRoleID,
        twoFactorEnabled: user.twoFactorEnabled,
        unlockesIn: user.unlockesIn,
        username: user.username,
        entityName: user.entityName,

        regionalSettings: {
          currenciesAvailable: user.regionalSettings.currenciesAvailable,
          defaultCurrency: user.regionalSettings.defaultCurrency,
          languagesAvailable: user.regionalSettings.languagesAvailable,
          timezone: user.regionalSettings.timezone,
          themeCode: user.regionalSettings.themeCode
        },

        // ✅ Only override if all exist
        ...(user.entityType &&
          user.entityID &&
          user.entityCode && {
            entityType: user.entityType,
            entityID: user.entityID,
            entityCode: user.entityCode
          }),

        selectedResourceType: {
          label: 'ACCOMMODATION',
          labelFA: 'هتل'
        }
      }))
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    if (!params.id) return

    getUser({
      variables: { userID: params.id }
    })
  }, [params.id])

  const [saveUser, { loading: loadingForUser }] = useMutation(
    params.id ? MODIFY_USER_MUTATION : GENERATE_USER_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        if (params.id) {
          toast.success(res.modifyUser.message)
        } else {
          toast.success(res.generateUser.message)
        }
        history.push(`/${lang}/access-management/user-management`)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const handleSubmit = (values: UserForCreateAndModify) => {
    if (params.id) {
      const {
        lastLogin,
        entityName,
        locked,
        unlockesIn,
        failedAttempts,
        selectedResourceType,
        displayRoleID,
        ...newValues
      } = values

      const updatedResources = newValues.resources.map(
        ({
          AccommodationName,
          TenantName,
          displayEntityCode,
          entityName: entityNameInResource,
          ValidFrom,
          ValidUntil,
          ...resource
        }) => ({
          ...resource
        })
      )

      newValues.resources = updatedResources

      return saveUser({
        variables: {
          userID: params.id,
          input: newValues
        }
      })
    } else {
      const {
        lastLogin,
        entityName,
        locked,
        unlockesIn,
        failedAttempts,
        selectedResourceType,
        displayRoleID,
        ...newValues
      } = values

      const updatedResources = newValues.resources.map(
        ({
          AccommodationName,
          TenantName,
          displayEntityCode,
          entityName: entityNameInResource,
          ValidFrom,
          ValidUntil,
          ...resource
        }) => ({
          ...resource
        })
      )

      newValues.resources = updatedResources

      return saveUser({
        variables: {
          input: newValues
        }
      })
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={userFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => (
        <>
          <Form>
            <UserHeader params={params} />
            <UserBasicInfo
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
              params={params}
            />
            <UserSetting
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
              params={params}
            />
            <UserSecurity
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
            />
            <UserRole
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
              params={params}
            />
            {/* <UserTheme
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
            /> */}
            <UserFooter
              isSubmitting={isSubmitting}
              loadingForUser={loadingForUser}
            />
          </Form>
          <AdotelSkeleton
            open={loadingForUsers}
            circlesColor={theme.palette.primary.main}
            numberOfCircles={4}
          />
        </>
      )}
    </Formik>
  )
}
